/** @format */
import * as Sentry from "@sentry/react";

import { scannerConfigurationUrl } from "constants/apiurls";
import { DB_RESPONSE_OK } from "constants/dbStatuses";
import { expiredPath } from "constants/paths";
import { ConfigurationsContext } from "Context/configurations";
import { NetworkStatusContext } from "Context/networkStatus";
import { useNotificationContext } from "Context/notificationContext";
import { TicketStorageContext } from "Context/ticketStorageContext";
import { requestTicketUpdateDataFromIndexedDb } from "indexedDb/operations";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { encodeQueryParams } from "util/api";
import { Localized } from "@fluent/react";
import { syncNewRevocations, syncNewScans, syncNewTickets, syncOfflineScans } from "util/sync";
import { ServiceWorkerContext } from "Context/serviceWorker";

const AutoUpdater = () => {
	const history = useHistory();
	// @ts-expect-error improperly typed context
	const { scannerConfiguration, scanSettings, persistScannerConfigurations, boothMode } =
		useContext(ConfigurationsContext);
	const { autoUpdate, updateInterval } = scanSettings;
	const { available, updateAvailable, swUpdate, skipWaitingAndReload } = useContext(ServiceWorkerContext);

	const online = useContext(NetworkStatusContext);
	const {
		// @ts-expect-error improperly typed context
		initialized,
		// @ts-expect-error improperly typed context
		lastSync,
		// @ts-expect-error improperly typed context
		setLastSync,
		// @ts-expect-error improperly typed context
		updateTicketAmountFromDatabaseToState,
		// @ts-expect-error improperly typed context
		updateUniqueScansAmountFromDatabaseToState,
		// @ts-expect-error improperly typed context
		updateUnsyncedScanAmountFromDatabase,
	} = useContext(TicketStorageContext);

	// @ts-expect-error improperly typed context
	const { handleErrorNotification, handleSuccessNotification } = useNotificationContext();
	const [needUpdate, setNeedUpdate] = useState(false);

	const timeout = useRef<number | null>(null);
	const updating = useRef(false);

	const updateConfig = useCallback(async () => {
		const apikey = scannerConfiguration.apikey;
		if (!apikey) return;
		const resp = await fetch(`${scannerConfigurationUrl}${encodeQueryParams({ apikey })}`);
		if (resp.ok) {
			const incomingData = await resp.json();
			await persistScannerConfigurations(incomingData);
		} else if (online) {
			// If we're online but the config endpoint refuses, the apikey is most likely expired.
			history.push(expiredPath);
		}
	}, [scannerConfiguration.apikey, persistScannerConfigurations, history, online]);

	const reportSyncError = useCallback(
		(err: unknown) => {
			Sentry.captureException(err);
			handleErrorNotification({
				title: (
					<Localized
						id="apirequest-error"
						vars={{ status: String(err) }}
					>{`Error synchronizing data`}</Localized>
				),
				children: JSON.stringify(err instanceof Error ? { name: err.name, message: err.message } : err),
			});
		},
		[handleErrorNotification],
	);

	// Main-thread fallback: same effect as the SW path, just on this thread.
	// Used when there is no controlling SW or when the SW call fails/times out.
	const runMainThreadSync = useCallback(async () => {
		const { status, data } = await requestTicketUpdateDataFromIndexedDb();
		if (status !== DB_RESPONSE_OK) return;
		const { configuration, lastScanned, lastTicket, lastRevocation } = data;
		const apikey = configuration.apikey;
		if (!apikey) return;

		await syncOfflineScans(apikey, scanSettings.device_name);
		updateUnsyncedScanAmountFromDatabase(0);

		if (!configuration.booth_mode) {
			const added = await syncNewTickets(apikey, lastTicket);
			updateTicketAmountFromDatabaseToState((n: number) => n + added);
			const newScans = await syncNewScans(apikey, lastScanned);
			updateUniqueScansAmountFromDatabaseToState((n: number) => n + newScans);
			await syncNewRevocations(apikey, lastRevocation);
		}
	}, [
		scanSettings.device_name,
		updateUnsyncedScanAmountFromDatabase,
		updateTicketAmountFromDatabaseToState,
		updateUniqueScansAmountFromDatabaseToState,
	]);

	const updateDone = useCallback(() => {
		const date = new Date().getTime();
		updating.current = false;
		if (timeout.current) window.clearTimeout(timeout.current);
		if (autoUpdate) {
			timeout.current = window.setTimeout(() => setNeedUpdate(true), updateInterval);
		}
		setLastSync(date);
		setNeedUpdate(false);
		updateConfig();
	}, [autoUpdate, updateConfig, updateInterval, setLastSync]);

	// Prompt the user once whenever a new SW is waiting. Replaces the SW→client
	// SW_WAIT broadcast — page-side learns about the waiting worker directly via
	// the registration, no custom message needed.
	useEffect(() => {
		if (!updateAvailable) return;
		handleSuccessNotification({
			title: <Localized id="update-pending">{`Update ready, reload?`}</Localized>,
			onClick: () => skipWaitingAndReload(),
		});
	}, [updateAvailable, handleSuccessNotification, skipWaitingAndReload]);

	useEffect(() => {
		if (!autoUpdate) {
			if (timeout.current) {
				window.clearTimeout(timeout.current);
				timeout.current = null;
			}
		} else if (!timeout.current) {
			timeout.current = window.setTimeout(
				() => setNeedUpdate(true),
				Math.max(0, updateInterval - (new Date().getTime() - lastSync)),
			);
		}
	}, [autoUpdate, lastSync, updateInterval]);

	useEffect(() => {
		if (!needUpdate || updating.current || !online || !initialized) return;
		const apikey = scannerConfiguration.apikey;
		if (!apikey) return;
		updating.current = true;

		const run = async () => {
			// Prefer SW offloading; on any failure (no controller, timeout, SW error)
			// transparently fall through to the main-thread path.
			if (available) {
				try {
					const target = boothMode ? "OFFLINE_SCANS" : "ALL";
					const result = await swUpdate(target, apikey, scanSettings.device_name);
					if (result.busy) {
						// Another tab's SW is mid-flight; treat this tick as a no-op.
						updateDone();
						return;
					}
					if (result.error) {
						reportSyncError(result.error);
						updateDone();
						return;
					}
					if (result.offlineScansSent !== undefined) updateUnsyncedScanAmountFromDatabase(0);
					if (result.newTickets) {
						updateTicketAmountFromDatabaseToState((n: number) => n + (result.newTickets ?? 0));
					}
					if (result.newScans) {
						updateUniqueScansAmountFromDatabaseToState((n: number) => n + (result.newScans ?? 0));
					}
					updateDone();
					return;
				} catch (err) {
					// Timeout / postMessage threw / controller vanished — fall back.
					console.warn("SW sync failed, falling back to main thread:", err);
					Sentry.captureException(err);
				}
			}
			try {
				await runMainThreadSync();
			} catch (err) {
				reportSyncError(err);
			}
			updateDone();
		};

		run();
	}, [
		available,
		boothMode,
		initialized,
		needUpdate,
		online,
		reportSyncError,
		runMainThreadSync,
		scanSettings.device_name,
		scannerConfiguration.apikey,
		swUpdate,
		updateDone,
		updateTicketAmountFromDatabaseToState,
		updateUniqueScansAmountFromDatabaseToState,
		updateUnsyncedScanAmountFromDatabase,
	]);

	return null;
};

export default AutoUpdater;
